/**
 * WorldLiveMap — a live choropleth of the whole world.
 *
 *   - Each country is shaded by how many visitors it has in the selected range
 *     (data from /overview `worldCountries`, passed in as a prop).
 *   - Countries with someone browsing RIGHT NOW get a pulsing green marker
 *     (data from /admin/analytics/live `byWorldCountry`, polled here every 5s).
 *     Markers sit on the live visitor's averaged IP lat/lon when known, and fall
 *     back to the country's centroid otherwise.
 *
 * Geometry is bundled (src/data/worldCountries.json) — Natural Earth 110m shapes
 * pre-projected to a plain equirectangular projection, so there's no map library
 * or runtime projection. The same lon/lat → x/y formula is reused here to place
 * live markers. Countries are keyed by ISO 3166-1 alpha-2 (matches country_code).
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import WORLD from '../../data/worldCountries.json';

// Equirectangular projection into the bundled geometry's coordinate space.
const projX = (lon) => ((Number(lon) + 180) / 360) * WORLD.width;
const projY = (lat) => ((90 - Number(lat)) / 180) * WORLD.height;

// Vertex-average centroid of a path (good enough to anchor a marker).
function centroidOf(d) {
  const nums = (d.match(/-?\d+\.?\d*/g) || []).map(Number);
  let sx = 0, sy = 0, n = 0;
  for (let i = 0; i + 1 < nums.length; i += 2) { sx += nums[i]; sy += nums[i + 1]; n++; }
  return n ? [sx / n, sy / n] : [0, 0];
}

// Linear blend between two hex colors.
function mix(a, b, t) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((x, i) => Math.round(x + (pb[i] - x) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

// ISO alpha-2 → flag emoji (regional-indicator letters).
const flag = (cc) =>
  (cc && cc.length === 2 && /^[A-Za-z]{2}$/.test(cc))
    ? String.fromCodePoint(...[...cc.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65))
    : '';

const EMPTY = '#eef2f6';   // slate-100, countries with no traffic
const LOW = '#99f6e4';     // teal-200
const HIGH = '#0f766e';    // teal-700

export default function WorldLiveMap({ countries = [] }) {
  const [live, setLive] = useState({ byCountry: [], count: 0 });
  const [hover, setHover] = useState(null);

  // Poll live world markers every 5s while visible.
  useEffect(() => {
    let alive = true;
    const load = () => api.get('/admin/analytics/live')
      .then(r => {
        if (!alive) return;
        const bw = r.data.byWorldCountry || [];
        setLive({ byCountry: bw, count: bw.reduce((s, x) => s + x.count, 0) });
      })
      .catch(() => {});
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const centroids = useMemo(() => {
    const m = {};
    for (const [code, c] of Object.entries(WORLD.countries)) m[code] = centroidOf(c.path);
    return m;
  }, []);

  const byCode = useMemo(() => {
    const m = {};
    for (const c of countries) m[c.code] = c;
    return m;
  }, [countries]);

  const maxVisitors = useMemo(
    () => Math.max(1, ...countries.map(c => Number(c.visitors) || 0)),
    [countries]
  );

  const liveByCode = useMemo(() => {
    const m = {};
    for (const c of live.byCountry) m[c.code] = c;
    return m;
  }, [live]);

  const fillFor = (code) => {
    const v = Number(byCode[code]?.visitors) || 0;
    if (!v) return EMPTY;
    const t = v / maxVisitors;            // 0..1
    return mix(LOW, HIGH, Math.sqrt(t));  // sqrt so small counts stay visible
  };

  // Marker anchor: averaged live lat/lon when known, else the country centroid.
  const markerXY = (entry) => {
    if (entry.lat != null && entry.lon != null) return [projX(entry.lon), projY(entry.lat)];
    return centroids[entry.code] || null;
  };

  const totalVisitors = countries.reduce((s, x) => s + (Number(x.visitors) || 0), 0);
  const topCountries = [...countries].sort((a, b) => b.visitors - a.visitors).slice(0, 8);

  const hovered = hover
    ? (byCode[hover] || { name: WORLD.countries[hover]?.name, visitors: 0, views: 0, code: hover })
    : null;

  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-gray-700">Where your visitors are — worldwide</h3>
        <div className="flex items-center gap-3 text-xs">
          {live.count > 0 && (
            <span className="flex items-center gap-1 text-green-600 font-medium">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              {live.count} live worldwide now
            </span>
          )}
          <span className="text-gray-400">{totalVisitors.toLocaleString()} visitors</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_200px] gap-4 items-start">
        {/* Map */}
        <div className="relative">
          <svg viewBox={WORLD.viewBox} className="w-full h-auto" style={{ maxHeight: 460, background: '#f8fafc' }}>
            {Object.entries(WORLD.countries).map(([code, c]) => (
              <path
                key={code}
                d={c.path}
                fill={fillFor(code)}
                stroke="#ffffff"
                strokeWidth={0.4}
                style={{ cursor: 'pointer', transition: 'fill .2s' }}
                onMouseEnter={() => setHover(code)}
                onMouseLeave={() => setHover(h => (h === code ? null : h))}
              >
                <title>{`${c.name}: ${(byCode[code]?.visitors || 0)} visitors`}</title>
              </path>
            ))}

            {/* Live pulsing markers */}
            {live.byCountry.map((entry) => {
              const xy = markerXY(entry);
              if (!xy) return null;
              const count = entry.count;
              return (
                <g key={'live-' + entry.code} pointerEvents="none">
                  <circle cx={xy[0]} cy={xy[1]} r={5 + Math.min(count, 6)} fill="#22c55e" opacity="0.25">
                    <animate attributeName="r" values={`${5 + Math.min(count, 6)};${11 + Math.min(count, 6)};${5 + Math.min(count, 6)}`} dur="1.6s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.35;0;0.35" dur="1.6s" repeatCount="indefinite" />
                  </circle>
                  <circle cx={xy[0]} cy={xy[1]} r="3" fill="#16a34a" stroke="#fff" strokeWidth="0.8" />
                </g>
              );
            })}
          </svg>

          {/* Hover readout */}
          {hovered && (
            <div className="absolute top-2 left-2 bg-gray-900/90 text-white text-xs rounded-lg px-3 py-2 pointer-events-none">
              <div className="font-semibold">{flag(hovered.code)} {hovered.name}</div>
              <div className="text-gray-300">{(hovered.visitors || 0).toLocaleString()} visitors · {(hovered.views || 0).toLocaleString()} views</div>
              {liveByCode[hover]?.count > 0 && <div className="text-green-400">{liveByCode[hover].count} live now</div>}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400">
            <span>Fewer</span>
            <div className="h-2 w-32 rounded" style={{ background: `linear-gradient(to right, ${EMPTY}, ${LOW}, ${HIGH})` }} />
            <span>More</span>
            <span className="ml-3 flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-green-500 inline-block" /> live now</span>
          </div>
        </div>

        {/* Top countries list */}
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-2">Top countries</div>
          {topCountries.length === 0 ? (
            <div className="text-xs text-gray-400">No visitors yet</div>
          ) : (
            <div className="space-y-1.5">
              {topCountries.map(c => (
                <div key={c.code}
                  className={'flex items-center justify-between text-sm px-2 py-1 rounded ' + (hover === c.code ? 'bg-teal-50' : '')}
                  onMouseEnter={() => setHover(c.code)} onMouseLeave={() => setHover(null)}>
                  <span className="flex items-center gap-2 text-gray-700 min-w-0">
                    <span className="inline-block h-3 w-3 rounded-sm flex-shrink-0" style={{ background: fillFor(c.code) }} />
                    <span className="truncate">{flag(c.code)} {c.name}</span>
                    {liveByCode[c.code]?.count > 0 && <span className="h-1.5 w-1.5 rounded-full bg-green-500 inline-block flex-shrink-0" />}
                  </span>
                  <span className="tabular-nums text-gray-500 flex-shrink-0">{Number(c.visitors).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
